<template>
    <FormSection title="Demographics" :full-height="false" :application-id="referral.application">
        <template v-slot:content>
            <section class="row py-3 pb-0 g-0">
                <div class="col-12 col-lg-6 px-4 g-0">
                    <div class="col-12 py-2">
                        <div class="row">
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_First_Name_Label" for="referral-first-name">
                                    First Name
                                    <span class="text-danger">*</span>
                                </label>
                                <input
                                    id="Referral_First_Name"
                                    name="referral-first-name"
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter First Name"
                                    @blur="v$.referral.firstName.$touch"
                                    :class="`${!v$.referral.firstName.$error ? '' : 'field-invalid'}`"
                                    v-model="referral.firstName"
                                />
                                <span v-if="v$.referral.firstName.$error" class="field-invalid"
                                    >First name is required.</span
                                >
                            </div>
                            <div class="form-group col-4 col-lg-6 pe-lg-2">
                                <label for="referral-middle-initial"> M.I. </label>
                                <input
                                    name="referral-middle-initial"
                                    class="form-control"
                                    maxlength="1"
                                    type="text"
                                    placeholder="Enter M.I."
                                    v-model="referral.middleName"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Last_Name_Label" for="referral-last-name">
                                    Last Name
                                    <span class="text-danger">*</span>
                                </label>
                                <input
                                    id="Referral_Last_Name"
                                    name="referral-last-name"
                                    class="form-control"
                                    maxlength="50"
                                    placeholder="Enter Last Name"
                                    type="text"
                                    @blur="v$.referral.lastName.$touch"
                                    :class="`${!v$.referral.lastName.$error ? '' : 'field-invalid'}`"
                                    v-model="referral.lastName"
                                />
                                <span v-if="v$.referral.lastName.$error" class="field-invalid"
                                    >Last name is required.</span
                                >
                            </div>
                        </div>
                    </div>
                    <div class="col-12 py-2" v-if="referral.primaryAddress">
                        <div class="row">
                            <ReferralAddressForm
                                v-model:address="referral.primaryAddress"
                                :application="referral.application"
                                :account-id="referral.accountId"
                                :is-primary-address="true"
                                :provider-id="providerId"
                                v-on:updateAddressDetails="updateAddressDetails"
                                v-on:updateAddressCountry="updateAddressCountry"
                                v-on:updateAddressState="updateAddressState"
                                ref="address"
                            />
                            <div class="col-12 d-flex align-items-center">
                                <div class="me-2 ai-validate-address-btn">
                                    <button
                                        class="button fr ai-validate-btn"
                                        @click="validateAddress()"
                                        v-if="!referral.primaryAddress.isValidated"
                                    >
                                        Validate Patient Primary Address
                                    </button>
                                </div>
                                <div>
                                    <h6
                                        :class="`${
                                            referral.primaryAddress.isBypassed ? 'text-warning' : 'text-danger'
                                        } fw-bold`"
                                        v-if="!referral.primaryAddress.isValidated"                            
                                    >
                                        <i class="fas fa-exclamation-triangle me-2"></i>
                                        <span
                                            >Address
                                            {{
                                                referral.primaryAddress.isBypassed
                                                    ? "Validation Bypassed"
                                                    : "Not Validated"
                                            }}
                                        </span>
                                    </h6>
                                    <h6 class="text-success fw-bold" v-else>
                                        <i class="fas fa-check-circle"></i>
                                        <span> Address Validated </span>
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        class="col-12 p-2"
                        v-if="hasTestAgency && referral.application === applicationEnum.HomeCare && isEdit"
                    >
                        <div class="form-group d-flex fw-bold justify-content-between">
                            <span class="me-3"> Branch Name</span>
                            <span> {{ branchName }}</span>
                        </div>
                        <div class="mt-2 d-flex flex-column">
                            <span class="font-size-3xs fw-normal">
                                Select View Branch History to view or modify the branch associated to this client.
                                Select Add Branch to add a new branch and an associated effective date.
                            </span>
                            <div class="d-flex mt-2">
                                <button
                                    type="button"
                                    class="btn btn-primary btn-md me-2"
                                    @click="handleViewBranchHistory"
                                >
                                    View Branch History
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-primary btn-md"
                                    v-if="canAddBranches"
                                    @click="handleAddBranch"
                                >
                                    Add Branch
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 py-2" v-else>
                        <div class="form-group row col-12 col-lg-12 pe-lg-2">
                            <label id="Referral_Agency_Branch_Label" for="agency-location-id">
                                Agency Branch
                                <span class="text-danger">*</span>
                            </label>
                            <select
                                id="Referral_Agency_Branch"
                                name="agency-location-id"
                                class="form-select"
                                v-model="referral.agencyLocationId"
                                @blur="v$.referral.agencyLocationId.$touch"
                                :class="`${!v$.referral.agencyLocationId.$error ? '' : 'field-invalid'}`"
                            >
                                <option :value="null">Select Branch</option>
                                <option v-for="branch in branches" :value="branch.id" :key="branch.id">
                                    {{ branch.name }}
                                </option>
                            </select>
                            <span v-if="v$.referral.agencyLocationId.$error" class="field-invalid"
                                >Agency Branch is required.</span
                            >
                        </div>
                    </div>
                    <div class="col-12 py-2">
                        <div class="row">
                            <div
                                class="col-12 col-lg-12 pe-lg-2"
                                v-for="(phoneNum, index) of referral.phoneNumbers"
                                :key="phoneNum.ordinal"
                            >
                                <div class="form-group">
                                    <label for="referral-mobile-number">
                                        {{
                                            index == 0
                                                ? "Primary Phone"
                                                : index == 1
                                                  ? "Secondary Phone"
                                                  : "Tertiary Phone"
                                        }}
                                        <span class="text-danger">*</span>
                                    </label>
                                    <PhoneNumber
                                        :enable-country="true"
                                        v-model:value="phoneNum.number"
                                        v-model:extension="phoneNum.extension"
                                        v-model:type="phoneNum.type"
                                        v-model:country="phoneNum.country"
                                        :phone-type-options="phoneNumberTypeOptionsConfig"
                                        v-number-only
                                        v-on:blurredInput="validateNumberTouch[index] = true"
                                    />
                                    <span
                                        v-if="checkPhoneValidation && !validateNumberTouch[index]"
                                        v-for="error in v$.referral.phoneNumbers.$each.$response.$errors[index]?.number"
                                        :key="error"
                                        class="field-invalid"
                                    >
                                        {{
                                            error.$message === "Value is required"
                                                ? "Phone number is required."
                                                : "10 Digit phone is required."
                                        }}
                                    </span>
                                    <span
                                        v-else-if="
                                            validateNumberTouch[index] &&
                                            v$.referral.phoneNumbers.$each.$message[index].length > 0
                                        "
                                        class="field-invalid"
                                    >
                                        {{
                                            v$.referral.phoneNumbers.$each.$message[index] == "Value is required"
                                                ? "Phone number is required."
                                                : "10 Digit phone is required."
                                        }}
                                    </span>
                                    <div class="d-flex justify-content-end">
                                        <div class="me-2 mt-2" v-if="showAddAlternatebtn(index)">
                                            <button
                                                class="btn btn-xs btn-primary mb-2"
                                                @click="addAlternateContact(index)"
                                            >
                                                Add Alternate Number
                                            </button>
                                        </div>
                                        <div class="mr mt-2" v-if="index > 0">
                                            <button
                                                class="btn btn-xs btn-danger mb-2"
                                                @click="removeAlternateContact(index)"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-email"> Email </label>
                                <input
                                    name="referral-email"
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Seperate mulitple emails with ;"
                                    v-model="referral.emailAddress"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-lg-6 px-4 g-0">
                    <div class="col-12 py-2">
                        <div class="row">
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="date-of-birth" id="Referral_Date_Of_Birth_Label">
                                    Date of Birth
                                    <span class="text-danger">*</span>
                                </label>
                                <DatePicker :getDate="changeDate">
                                    <div class="input-group date datepickerInput">
                                        <input
                                            id="Referral_Date_Of_Birth"
                                            name="referralHomeCareDOB"
                                            type="text"
                                            class="form-control"
                                            placeholder="MM/DD/YYYY"
                                            v-model="formattedDob"
                                            autocomplete="off"
                                            @click="resetIsNoConflictValue()"
                                            @blur="v$.referral.dob.$touch"
                                            :class="`${!v$.referral.dob.$error ? '' : 'field-invalid'}`"
                                            date-format-only
                                        />
                                        <div class="input-group-addon input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                        </div>
                                    </div>
                                </DatePicker>
                                <span v-if="v$.referral.dob.$error" class="field-invalid"
                                    >Date Of Birth is required.</span
                                >
                                <div class="me-2 mt-2">
                                    <button
                                        class="btn btn-xs btn-primary mb-2"
                                        @click="checkClientConflict()"
                                        v-if="!hasNoConflict"
                                    >
                                        Check For Client Conflict
                                    </button>
                                    <text v-if="hasNoConflict">There is no conflict!</text>
                                </div>
                            </div>
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Gender_Label" for="referral-gender"
                                    >Sex
                                    <span class="text-danger">*</span>
                                </label>
                                <select
                                    id="Referral_Gender"
                                    class="form-select"
                                    name="referral-gender"
                                    v-model="referral.gender"
                                    @blur="v$.referral.gender.$touch"
                                    :class="`${!v$.referral.gender.$error ? '' : 'field-invalid'}`"
                                >
                                    <option :value="null">Select Gender</option>
                                    <option
                                        v-for="[key, value] in enumToMap(GendersEnum)"
                                        :value="value.value"
                                        :key="key"
                                    >
                                        {{ value.name }}
                                    </option>
                                </select>
                                <span v-if="v$.referral.gender.$error" class="field-invalid">Gender is required.</span>
                            </div>
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Gender_Label" for="referral-gender"> Gender Pronouns </label>
                                <MultiSelectDropdown
                                    :options="genderPronounOptions"
                                    :selected-items="referral.genderPronounsArray"
                                    v-on:selectedItemsUpdated="selectedItemsUpdated"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Medicaid_Label" for="referral-medicaid"> Medicaid Number </label>
                                <input
                                    id="Referral_Medicaid"
                                    name="referral-medicaid"
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter Medicaid Number"
                                    v-model="referral.medicaidNumber"
                                />
                            </div>
                            <div class="form-group col-4 col-lg-6 pe-lg-2">
                                <label id="Referral_Alternate_Medicaid_Label" for="referral-alternate-medicaid"
                                    >Alternate Medicaid Number</label
                                >
                                <input
                                    id="Referral_Alternate_Medicaid"
                                    name="referral-alternate-medicaid"
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter Alternate Medicaid Number"
                                    v-model="referral.alternateMedicaidNumber"
                                />
                            </div>
                            <div class="form-group col-4 col-lg-6 pe-lg-2">
                                <label for="referral-social-security-number"> Social Security Number </label>
                                <input
                                    name="referral-social-security-number"
                                    maxlength="9"
                                    class="form-control"
                                    type="text"
                                    placeholder="Enter SSN"
                                    v-model="referral.ssn"
                                    v-number-only
                                />
                            </div>
                        </div>
                    </div>
                    <div class="col-12 py-2">
                        <div class="row">
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label id="Referral_Payrate_Label" for="referral-payrate">Pay Rate Municipality </label>
                                <select
                                    id="Referral_PayRates"
                                    class="form-select"
                                    name="referral-payrates"
                                    v-model="referral.payRateMunicipalityId"
                                >
                                    <option :value="null">Select Municipality</option>
                                    <template v-if="muncipalityPayRates[referral.agencyLocationId]">
                                        <option
                                            v-for="payRate in muncipalityPayRates[referral.agencyLocationId]"
                                            :value="payRate.Id"
                                            :key="payRate"
                                        >
                                            {{ payRate.Name }} {{ payRate.Code ? `(${payRate.Code})` : "" }}
                                        </option>
                                    </template>
                                </select>
                                <p
                                    class="mb-0"
                                    v-if="
                                        referral.agencyLocationId &&
                                        !muncipalityPayRates[referral.agencyLocationId]?.length
                                    "
                                >
                                    No pay rate municipalities have been added for this referral's location. If
                                    required, please contact your agency administrator to configure pay rate
                                    municipalities for Addison in Company Setup.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 py-2">
                        <div class="row">
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-tags"> Tags</label>
                                <TagsInput
                                    :data="tags"
                                    :tags="selectedTags"
                                    placeholder="Select Tags"
                                    aria-autocomplete="off"
                                    :open-on-focus="true"
                                    v-on:updateTags="updateTags"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="col-12 py-2" v-if="!referral.isNonMedical">
                        <div class="row">
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Gender_Label" for="referral-gender">Do Not Resuscitate </label>
                                <select
                                    id="Referral_PayRates"
                                    class="form-select"
                                    name="referral-payrates"
                                    v-model="referral.isDNR"
                                >
                                    <option :value="false">No</option>
                                    <option :value="true">Yes</option>
                                </select>
                            </div>
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Gender_Label" for="referral-gender">Marital Status </label>
                                <select
                                    id="Referral_PayRates"
                                    class="form-select"
                                    name="referral-payrates"
                                    v-model="referral.maritalStatus"
                                >
                                    <option :value="null">Select Marital Status</option>
                                    <option
                                        v-for="[key, value] in enumToMap(MaritalStatusesEnum)"
                                        :value="value.value"
                                        :key="key"
                                    >
                                        {{ value.name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 py-2" v-if="!referral.isNonMedical">
                        <div class="row">
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Height" for="referral-height">Height </label>
                                <div class="form-group d-flex col-5">
                                    <input
                                        name="referral-social-security-number"
                                        maxlength="3"
                                        class="form-control col-lg-6"
                                        type="text"
                                        placeholder="Enter Height"
                                        v-model="referral.height"
                                        number-only
                                    />
                                    <select
                                        id="Referral_PayRates"
                                        class="form-select col-12 col-lg-5 col-md-6 px-2"
                                        name="referral-payrates"
                                        v-model="referral.heightMetric"
                                    >
                                        <option :value="0">in</option>
                                        <option :value="1">cm</option>
                                    </select>
                                </div>
                            </div>
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Weight" for="referral-weight">Weight </label>
                                <div class="form-group d-flex col-5">
                                    <input
                                        name="referral-social-security-number"
                                        maxlength="3"
                                        class="form-control col-lg-6"
                                        type="text"
                                        placeholder="Enter Weight"
                                        v-model="referral.weight"
                                        v-number-only
                                    />
                                    <select
                                        id="Referral_PayRates"
                                        class="form-select col-12 col-lg-5 col-md-6 px-2"
                                        name="referral-payrates"
                                        v-model="referral.weightMetric"
                                    >
                                        <option :value="0">lb</option>
                                        <option :value="1">kg</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </template>
    </FormSection>
</template>

<style src="./DemographicsInformation.scss"></style>
<script lang="ts" src="./DemographicsInformation.ts"></script>
